import React, { useEffect, useState } from 'react';
import storeApi from '../../../../../services/storeApi';
import InputMask from 'react-input-mask';
import { Upload, Switch } from 'antd';
import { TWButton, TWModal } from '../../../../../components/ui';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import AddressAutocomplete from '../../../../../components/address-autocomplete/address-autocomplete';
import { apartmentValue } from '../../../../../utils/apartment';

// Apt / Suite is cleaned once, as the form loads — never per render: trimming
// the typed value each keystroke ate its spaces ("Suite 200" saved "Suite200",
// owner report 2026-09-25).
const toForm = (storeDetail) => ({
  ...storeDetail,
  latitude: storeDetail.latitude ? storeDetail.latitude : '',
  longitude: storeDetail.longitude ? storeDetail.longitude : '',
  apartment: apartmentValue(storeDetail.apartment),
});

const StoreUpsertModal = ({
  storeDetail,
  onCloseUpsert,
  show,
  checkUpdate,
}) => {
  const [form, setForm] = useState(() => toForm(storeDetail));
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    name: false,
    responsiblename: false,
    responsiblephone: false,
    address: false,
    phone: false,
    slug: false,
  });

  useEffect(() => {
    setForm(toForm(storeDetail));
  }, [storeDetail]);

  const onFormSubmit = async () => {
    if (
      !form.name ||
      !form.responsiblename ||
      !form.responsiblephone ||
      !form.address ||
      !form.phone
    ) {
      setErrors((prev) => ({
        ...prev,
        name: !form.name ? true : false,
        responsiblename: !form.responsiblename ? true : false,
        responsiblephone: !form.responsiblephone ? true : false,
        address: !form.address ? true : false,
        phone: !form.phone ? true : false,
      }));
      return;
    } else {
      setErrors((prev) => ({
        ...prev,
        responsiblephone: false,
        name: false,
        responsiblename: false,
        address: false,
        phone: false,
      }));
    }
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = { ...form, websiteclosedat: form.websiteclosedat || '' };
      const formData = new FormData();

      for (const key of Object.keys(body)) {
        formData.append(key, body[key]);
      }

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
      };

      const res = await storeApi.upsert(body, config);
      if (res.status === 200) {
        checkUpdate(true);
      }
    } catch (error) {
      checkUpdate(false, error.response.data);
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      onCloseUpsert();
    }
  };

  const onAddressSelect = (obj) => {
    if (typeof obj === 'object') {
      setForm((prev) => ({
        ...prev,
        address: obj.street,
        city: obj.city,
        state: obj.state,
        postalcode: obj.zipCode,
        longitude: obj?.longitude || '',
        latitude: obj?.latitude || '',
        apartment: obj?.apartment || '',
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        address: obj,
      }));
    }
  };

  const onFormChange = (val, key) => {
    if (key === 'name') {
      // Allow punctuation common in business names (e.g. "Sam's Kebob & Gyro's - Mediterranean Grill").
      const numAndLetters = /^[A-Za-z0-9 _'’&.,()/-]*$/;
      setForm((prev) => ({
        ...prev,
        [key]: numAndLetters.test(val) ? val : prev[key],
      }));
    } else {
      setForm((prev) => ({ ...prev, [key]: val }));
    }
  };

  const uploadButton = (
    <div>
      {loading.upsertLoading ? <LoadingOutlined /> : <PlusOutlined />}
      <div
        className="dark:text-slate-200"
        style={{
          marginTop: 8,
        }}
      >
        Upload
      </div>
    </div>
  );

  return (
    <TWModal
      open={show}
      onClose={onCloseUpsert}
      size="2xl"
      panelClassName="dark:text-slate-200"
    >
      <div className="space-y-5">
                <div className="grid grid-cols-8 gap-6">
                  <div className="col-span-8 sm:col-span-8">
                    <label
                      htmlFor="logo"
                      className="block pb text-sm font-medium text-gray-700 dark:text-slate-200"
                    >
                      Store Logo
                    </label>
                    <div className="mt-5">
                      <>
                        <Upload
                          multiple={false}
                          maxCount={1}
                          name="avatar"
                          onChange={(e) => onFormChange(e.file, 'storelogo')}
                          listType="picture-circle"
                          className="avatar-uploader"
                          beforeUpload={() => false}
                        >
                          {storeDetail.storelogo ? (
                            <img
                              src={storeDetail.storelogo}
                              alt="avatar"
                              className="min-h-[4rem] min-w-[4rem] h-16 w-16 rounded-full"
                              style={{
                                width: '100%',
                                height: '100%',
                              }}
                            />
                          ) : (
                            uploadButton
                          )}
                        </Upload>
                      </>
                      <div className="h-2 mt-1  text-xs">
                        {errors.imageid && (
                          <p className="text-red-700">Image can not be empty</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-8 sm:col-span-8">
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Store Name *
                    </label>
                    <input
                      value={form.name}
                      onChange={(e) => onFormChange(e.target.value, 'name')}
                      type="text"
                      name="name"
                      id="name"
                      className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                      placeholder="e.g. 'ABC Pizza'"
                    />
                    <div className="h-2 mt-1  text-xs">
                      {errors.name && (
                        <p className="text-red-700">
                          Store name can not be empty
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="col-span-8 sm:col-span-4">
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Store Phone Number *
                    </label>
                    <InputMask
                      value={form.phone}
                      mask="(999) 999 9999"
                      maskPlaceholder={' '}
                      onChange={(e) => onFormChange(e.target.value, 'phone')}
                      type="text"
                      name="phone"
                      id="phone"
                      className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                    />
                    <div className="h-2 mt-1  text-xs">
                      {errors.phone && (
                        <p className="text-red-700">
                          Store phone can not be empty
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="col-span-8 sm:col-span-8">
                    <h1 className="text-base font-extrabold text-gray-900 dark:text-zinc-200">
                      Store Address
                    </h1>
                    <p className=" text-sm text-gray-500 dark:text-zinc-300">
                      Your address will be displayed publicly!
                    </p>
                  </div>
                  <div className="col-span-8 sm:col-span-6">
                    <label
                      htmlFor="address"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Street Name
                    </label>
                    <AddressAutocomplete
                      value={form.address}
                      onChange={(e) => onAddressSelect(e)}
                    />

                    <div className="h-2 mt-1  text-xs">
                      {errors.address ? (
                        <p className="text-red-700">Address can not be empty</p>
                      ) : (
                        <p className="text-gray-500">
                          Please type your street name here e.g. '100 Main St'
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="col-span-8 sm:col-span-2">
                    <label
                      htmlFor="apartment"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Apt / Suite
                    </label>
                    <input
                      value={form.apartment || ''}
                      onChange={(e) =>
                        onFormChange(e.target.value, 'apartment')
                      }
                      type="text"
                      name="apartment"
                      id="apartment"
                      className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                      placeholder=""
                    />
                    <div className="h-2 mt-1  text-xs"></div>
                  </div>

                  <div className="col-span-8 grid grid-cols-8 gap-6">
                    <div className="col-span-8 sm:col-span-4">
                      <label
                        htmlFor="city"
                        className="block text-sm font-medium text-gray-700"
                      >
                        City
                      </label>
                      <input
                        value={form.city}
                        onChange={(e) => onFormChange(e.target.value, 'city')}
                        type="text"
                        name="city"
                        id="city"
                        className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                        placeholder=""
                      />
                      <div className="h-2 mt-1  text-xs"></div>
                    </div>
                    <div className="col-span-8 sm:col-span-2">
                      <label
                        htmlFor="state"
                        className="block text-sm font-medium text-gray-700"
                      >
                        State
                      </label>
                      <input
                        value={form.state}
                        onChange={(e) => onFormChange(e.target.value, 'state')}
                        type="text"
                        name="state"
                        id="state"
                        className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                        placeholder=""
                      />
                      <div className="h-2 mt-1  text-xs"></div>
                    </div>
                    <div className="col-span-8 sm:col-span-2">
                      <label
                        htmlFor="postalcode"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Zip Code
                      </label>
                      <input
                        value={form.postalcode}
                        onChange={(e) =>
                          onFormChange(e.target.value, 'postalcode')
                        }
                        type="text"
                        name="postalcode"
                        id="postalcode"
                        className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                        placeholder=""
                      />
                      <div className="h-2 mt-1  text-xs"></div>
                    </div>
                    <div className="col-span-8 sm:col-span-4">
                      <label
                        htmlFor="latitude"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Latitude
                      </label>
                      <input
                        value={form.latitude}
                        onChange={(e) =>
                          onFormChange(e.target.value, 'latitude')
                        }
                        type="text"
                        name="latitude"
                        id="latitude"
                        className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                        placeholder="e.g. '37.7749'"
                      />
                    </div>
                    <div className="col-span-8 sm:col-span-4">
                      <label
                        htmlFor="longitude"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Longitude
                      </label>
                      <input
                        value={form.longitude}
                        onChange={(e) =>
                          onFormChange(e.target.value, 'longitude')
                        }
                        type="text"
                        name="longitude"
                        id="longitude"
                        className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                        placeholder="e.g. '-122.4194'"
                      />
                    </div>
                  </div>

                  <div className="col-span-8 sm:col-span-8">
                    <h1 className="text-base font-extrabold text-gray-900 dark:text-zinc-200">
                      Store Manager
                    </h1>
                    <p className=" text-sm text-gray-500 dark:text-zinc-300">
                      This information <strong>will not</strong> be displayed
                      publicly.
                    </p>
                  </div>

                  <div className="col-span-8 sm:col-span-4">
                    <label
                      htmlFor="responsiblename"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Manager's Full Name
                    </label>
                    <input
                      value={form.responsiblename}
                      onChange={(e) =>
                        onFormChange(e.target.value, 'responsiblename')
                      }
                      type="text"
                      name="responsiblename"
                      id="responsiblename"
                      className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                      placeholder="e.g. 'John Doe'"
                    />
                    <div className="h-2 mt-1  text-xs">
                      {errors.responsiblename && (
                        <p className="text-red-700">
                          Manager's full name can not be empty
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="col-span-8 sm:col-span-4">
                    <label
                      htmlFor="responsiblephone"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Manager's Phone Number
                    </label>
                    <InputMask
                      value={form.responsiblephone}
                      mask="(999) 999 9999"
                      maskPlaceholder={' '}
                      onChange={(e) =>
                        onFormChange(e.target.value, 'responsiblephone')
                      }
                      type="text"
                      name="responsiblephone"
                      id="responsiblephone"
                      className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                    />
                    <div className="h-2 mt-1  text-xs">
                      {errors.responsiblephone && (
                        <p className="text-red-700">
                          Manager's phone number can not be empty
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-span-8 sm:col-span-8">
                  <div className="flex items-center">
                    <Switch
                      checked={form.tipdescriptionvisible || false}
                      onChange={(checked) =>
                        onFormChange(checked, 'tipdescriptionvisible')
                      }
                      className="bg-gray-300"
                    />
                    <label className="block text-sm font-medium text-gray-700 ml-2">
                      Show Tip Description
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                    Show tip description to customers
                  </p>
                </div>
                <div className="h-2 mt-1  text-xs"></div>
                <div className="mt-5 sm:mt-6 sm:grid z-10 sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <TWButton
                    variant="primary"
                    label="Update"
                    disabled={loading.upsertLoading}
                    loading={loading.upsertLoading}
                    onClick={() => onFormSubmit()}
                    className="w-full sm:col-start-2"
                  />
                  <TWButton
                    variant="default"
                    label="Cancel"
                    disabled={loading.upsertLoading}
                    onClick={() => onCloseUpsert()}
                    className="mt-3 w-full sm:col-start-1 sm:mt-0"
                  />
                </div>
      </div>
    </TWModal>
  );
};

export default StoreUpsertModal;
